/**
 * The fee engine.
 *
 * Every figure in this file is an integer number of pence. Money is never a float in this
 * codebase: floats lose halfpennies, and a lost halfpenny in the wrong direction is a broken
 * promise to a Runner.
 *
 * Pricing as ruled on 9 October 2026 (docs/BUILD_PROMPT.md, ruling 58), re-banded on
 * 16 October 2026 (ruling 63) and again on 20 October 2026 (ruling 73), every figure from
 * config/store.json:
 *
 * - An item charge on every unit: 50p, and 50p more for every whole £6 of the product's shop
 *   price (under £6 is 50p, £6 to £11.99 is £1, £12 to £17.99 is £1.50, and so on). The same on
 *   every plan, never removed. Prices shown to a Shopper always include it. A foundational
 *   rule (RULES.md, 20 October 2026): it may never be adjusted.
 * - Delivery by plan and by the value of the shopping, in bands (`fees.delivery.bands`): pay as
 *   you go £10.99 up to £30, £12.99 over £30 up to £60, £14.99 over £60; Ozi Membership (and an
 *   organisation's clients) £7.99 up to £60 and £9.99 over; Ozi Plus and Ozi Family and Carer
 *   £6.99 up to £60 and £8.99 over. The Runner's five pounds comes out of every one of them
 *   (Rule Two). The bands are read here, never written.
 * - A monthly order allowance on every monthly plan (`fees.delivery.allowance`, ruling 73):
 *   Membership 5 orders a calendar month, Plus 8, Family and Carer 5 for each person. The order
 *   after the allowance carries an extra delivery charge (£5.99), then two orders without it,
 *   then the extra again, and so on. Pay as you go has no allowance and never the extra.
 * - No single product over £60, and one order carries at most the whole-order goods cap.
 *
 * The Runner's five pounds is a constant in rules.ts, because it is untouched.
 *
 * The functions here are pure: same inputs, same outputs, no clock, no random, no I/O.
 */
import { packFirstFit } from './dispatch.js';
import { RUNNER_PAYMENT_PENCE } from './rules.js';
export const DELIVERY_PLANS = ['payg', 'membership', 'plus'];
/**
 * What the Runner buys and carries for these lines: each at its counter price where it has one,
 * otherwise its shop price (30 October 2026). For groceries with no partner terms, the goods.
 */
export function carriedGoodsPence(lines) {
    return lines.reduce((sum, line) => sum + (line.counterPricePence ?? line.shopPricePence) * line.quantity, 0);
}
/** A basket over the most one order carries. The Shopper is offered two deliveries. */
export class BasketOverMaximumError extends Error {
    goodsPence;
    maximumPence;
    constructor(goodsPence, maximumPence) {
        super(`A goods total of ${goodsPence}p is over the ${maximumPence}p most that one delivery carries. ` +
            `It can be split into two deliveries.`);
        this.goodsPence = goodsPence;
        this.maximumPence = maximumPence;
        this.name = 'BasketOverMaximumError';
    }
}
/** A single product over the most any one product may cost. It is not sold. */
export class ProductOverMaximumError extends Error {
    shopPricePence;
    maximumPence;
    constructor(shopPricePence, maximumPence) {
        super(`A product at ${shopPricePence}p is over the ${maximumPence}p most that any one product may cost.`);
        this.shopPricePence = shopPricePence;
        this.maximumPence = maximumPence;
        this.name = 'ProductOverMaximumError';
    }
}
function assertWholePence(value, label) {
    if (!Number.isInteger(value) || value < 0) {
        throw new TypeError(`${label} must be a whole, non-negative number of pence, received ${value}.`);
    }
}
/**
 * The item charge on one unit of a product, from its shop price: 50p, and 50p more for every
 * whole £6 (with the configured figures). Never removed by any plan.
 */
export function itemChargePence(shopPricePence, fees) {
    assertWholePence(shopPricePence, 'Shop price');
    const { basePence, stepPence, everyPence } = fees.itemCharge;
    return basePence + stepPence * Math.floor(shopPricePence / everyPence);
}
/**
 * The price a Shopper is shown or told for one unit: the shop price with its item charge
 * already in it. UK pricing law does not allow a compulsory charge to be added later.
 */
export function displayPricePence(shopPricePence, fees) {
    return shopPricePence + itemChargePence(shopPricePence, fees);
}
/** Whether one product may be sold at all: no single product over the configured most. */
export function productAllowed(shopPricePence, fees) {
    return shopPricePence <= fees.maximumProductPence;
}
/** Throws when a product is over the most any one product may cost. */
export function assertProductAllowed(shopPricePence, fees) {
    assertWholePence(shopPricePence, 'Shop price');
    if (!productAllowed(shopPricePence, fees)) {
        throw new ProductOverMaximumError(shopPricePence, fees.maximumProductPence);
    }
}
/** The item charges on a whole basket: each unit counted on its own. */
export function itemChargesForLines(lines, fees) {
    return lines.reduce((sum, line) => {
        assertWholePence(line.quantity, 'Quantity');
        return sum + itemChargePence(line.shopPricePence, fees) * line.quantity;
    }, 0);
}
/**
 * The delivery fee, in pence, for this much shopping (at shop prices) on this plan.
 *
 * Rule Four lives in this signature. The only inputs are the goods total, the plan the Shopper
 * chose and the configured fees. There is no time of day, no distance, no weather, no demand
 * and no order history, so there is nothing a future edit could multiply the fee by. A smaller
 * shop is cheaper to deliver on pay as you go, never dearer: there is no small order fee and
 * no minimum. A basket of one penny is priced and accepted.
 */
export function deliveryFeePence(goodsPence, plan, fees, 
/** What the Runner buys and carries, which the most one order carries is counted on. */
carriedPence = goodsPence) {
    assertWholePence(goodsPence, 'Goods total');
    if (carriedPence > fees.maximumOrderGoodsPence) {
        throw new BasketOverMaximumError(carriedPence, fees.maximumOrderGoodsPence);
    }
    return bandPrice(deliveryBandFor(goodsPence, fees), plan);
}
/** The band this much shopping (at shop prices) falls in: the first whose `upToPence` it is within. */
export function deliveryBandFor(goodsPence, fees) {
    assertWholePence(goodsPence, 'Goods total');
    const band = fees.delivery.bands.find((candidate) => candidate.upToPence === null || goodsPence <= candidate.upToPence);
    if (!band) {
        // The configuration parser requires a last band with no upper limit, so a parsed
        // configuration never gets here; a hand-made one is told plainly.
        throw new TypeError(`No delivery band covers a goods total of ${goodsPence}p.`);
    }
    return band;
}
/** What one plan pays in one band. */
export function bandPrice(band, plan) {
    switch (plan) {
        case 'plus':
            return band.plusPence;
        case 'membership':
            return band.membershipPence;
        case 'payg':
            return band.payAsYouGoPence;
        default: {
            const unknown = plan;
            throw new TypeError(`Unknown delivery plan: ${String(unknown)}.`);
        }
    }
}
/**
 * A plan's delivery prices as steps, with neighbouring bands that cost the same joined: on
 * Membership, £7.99 up to £30 and £7.99 up to £60 become one step, £7.99 up to £60, then £9.99
 * above. This is what the screens, Ozi and the documents say; the bands stay as configured.
 */
export function deliverySteps(plan, fees) {
    const steps = [];
    for (const band of fees.delivery.bands) {
        const pence = bandPrice(band, plan);
        const last = steps[steps.length - 1];
        if (last && last.pence === pence) {
            steps[steps.length - 1] = { upToPence: band.upToPence, pence };
        }
        else {
            steps.push({ upToPence: band.upToPence, pence });
        }
    }
    return steps;
}
/** The cheapest and dearest delivery on any plan, for "from £6.99 to £14.99". */
export function deliveryPriceRange(fees) {
    const all = fees.delivery.bands.flatMap((band) => DELIVERY_PLANS.map((plan) => bandPrice(band, plan)));
    return { lowestPence: Math.min(...all), highestPence: Math.max(...all) };
}
/**
 * A plan's delivery prices in one plain phrase, from its steps: "£10.99 for shopping up to
 * £30.00, £12.99 up to £60.00, and £14.99 over £60.00"; on Membership "£7.99 for shopping up to
 * £60.00, and £9.99 over £60.00"; a plan with one price reads "£7.99, whatever the size of the
 * shop". `formatMoney` is the caller's, so the currency symbol stays in configuration.
 */
export function deliveryStepsWords(plan, fees, formatMoney) {
    const steps = deliverySteps(plan, fees);
    const first = steps[0];
    if (!first)
        throw new TypeError('There are no delivery bands.');
    if (steps.length === 1)
        return `${formatMoney(first.pence)}, whatever the size of the shop`;
    return steps
        .map((step, index) => {
        if (step.upToPence === null) {
            const previous = steps[index - 1]?.upToPence ?? 0;
            return `and ${formatMoney(step.pence)} over ${formatMoney(previous)}`;
        }
        return index === 0
            ? `${formatMoney(step.pence)} for shopping up to ${formatMoney(step.upToPence)}`
            : `${formatMoney(step.pence)} up to ${formatMoney(step.upToPence)}`;
    })
        .join(', ');
}
/**
 * Goods, item charges, delivery and total together, so the API and the web never do this
 * arithmetic twice. Every product is checked against the most one product may cost.
 */
export function priceBasket(lines, plan, fees, allowanceExtraPence = 0) {
    for (const line of lines)
        assertProductAllowed(line.shopPricePence, fees);
    assertWholePence(allowanceExtraPence, 'Extra delivery charge');
    const goodsPence = lines.reduce((sum, line) => sum + line.shopPricePence * line.quantity, 0);
    const itemChargesPence = itemChargesForLines(lines, fees);
    const feePence = deliveryFeePence(goodsPence, plan, fees, carriedGoodsPence(lines));
    return {
        goodsPence,
        itemChargesPence,
        feePence,
        allowanceExtraPence,
        totalPence: goodsPence + itemChargesPence + feePence + allowanceExtraPence,
        plan,
    };
}
/* ------------------------------------------------------------- the monthly allowance */
/**
 * How many orders a month this plan includes (ruling 73), or null for pay as you go, which has
 * no allowance and never an extra charge. Family and Carer's figure is for each person.
 */
export function orderAllowanceFor(plan, allowance) {
    switch (plan) {
        case 'membership':
            return allowance.membershipOrders;
        case 'plus':
            return allowance.plusOrders;
        case 'family':
            return allowance.familyOrdersEach;
        case 'payg':
            return null;
        default: {
            const unknown = plan;
            throw new TypeError(`Unknown plan: ${String(unknown)}.`);
        }
    }
}
/**
 * The extra delivery charge on the Shopper's `orderNumberThisMonth`-th order of the calendar
 * month (1 for the first; the order being placed counts as the next number), on this plan
 * (ruling 73): `extraChargePence` when the order is past the allowance and
 * (n - allowance - 1) is a whole multiple of `extraEvery`; otherwise nothing. On Membership (5)
 * that is the 6th, 9th, 12th, 15th... order; on Plus (8) the 9th, 12th, 15th....
 *
 * Pure, like the rest of this file: the count of orders is the caller's, from the Shopper's
 * own orders. Rule Four is untouched: this is a fair-use allowance on a monthly plan, not a
 * price that moves with the time, the place or the demand.
 */
export function allowanceExtraPence(plan, orderNumberThisMonth, allowance) {
    if (!Number.isInteger(orderNumberThisMonth) || orderNumberThisMonth < 1) {
        throw new TypeError(`The order number this month must be a whole number from 1, received ${orderNumberThisMonth}.`);
    }
    const included = orderAllowanceFor(plan, allowance);
    if (included === null || orderNumberThisMonth <= included)
        return 0;
    return (orderNumberThisMonth - included - 1) % allowance.extraEvery === 0
        ? allowance.extraChargePence
        : 0;
}
/** "1st", "2nd", "3rd", "4th", "11th", "12th", "13th", "21st"... */
export function ordinalWord(n) {
    const lastTwo = n % 100;
    if (lastTwo >= 11 && lastTwo <= 13)
        return `${n}th`;
    switch (n % 10) {
        case 1:
            return `${n}st`;
        case 2:
            return `${n}nd`;
        case 3:
            return `${n}rd`;
        default:
            return `${n}th`;
    }
}
/**
 * What the Shopper is told before the one yes (Rule One) about the monthly allowance (ruling
 * 73), on the screen and aloud, or null when there is nothing to say: "This is your 6th order
 * this month. Your plan includes 5, so this one has an extra £5.99 delivery charge." On the last
 * order the plan includes, a gentle heads-up; past the allowance without the extra, a plain
 * note that this one has none. Pay as you go has nothing to say.
 */
export function allowanceWords(plan, orderNumberThisMonth, allowance, formatMoney) {
    const included = orderAllowanceFor(plan, allowance);
    if (included === null)
        return null;
    const extra = allowanceExtraPence(plan, orderNumberThisMonth, allowance);
    const includes = plan === 'family'
        ? `Your plan includes ${included} for each person`
        : `Your plan includes ${included}`;
    if (extra > 0) {
        return `This is your ${ordinalWord(orderNumberThisMonth)} order this month. ${includes}, so this one has an extra ${formatMoney(extra)} delivery charge.`;
    }
    if (orderNumberThisMonth === included) {
        return `This is your ${ordinalWord(orderNumberThisMonth)} order this month, the last one your plan includes. After it, some orders this month have an extra ${formatMoney(allowance.extraChargePence)} delivery charge, and you are always told before you say yes.`;
    }
    if (orderNumberThisMonth > included) {
        return `This is your ${ordinalWord(orderNumberThisMonth)} order this month. ${includes}, and this one has no extra delivery charge.`;
    }
    return null;
}
/**
 * A plan's allowance in one plain phrase, for the plans page, the terms and Ozi: "5 orders a
 * month; after that, an extra £5.99 delivery charge on the next order and every 3rd order after
 * it". Null for pay as you go.
 */
export function allowancePlanWords(plan, allowance, formatMoney) {
    const included = orderAllowanceFor(plan, allowance);
    if (included === null)
        return null;
    const each = plan === 'family' ? ' for each person' : '';
    const every = allowance.extraEvery === 1
        ? 'every order after it'
        : `every ${ordinalWord(allowance.extraEvery)} order after it`;
    return `${included} orders a month${each}; after that, an extra ${formatMoney(allowance.extraChargePence)} delivery charge on the next order and ${every}`;
}
/**
 * What the payment processor takes from a transaction, in pence: a percentage of the whole
 * charge, plus a fixed amount. Rounded up, so any report of what the platform keeps errs on
 * the low side.
 */
export function processorCostPence(totalTransactionPence, processor) {
    assertWholePence(totalTransactionPence, 'Transaction total');
    const percentagePart = Math.ceil((totalTransactionPence * processor.percentageBasisPoints) / 10_000);
    return percentagePart + processor.fixedPence;
}
/**
 * What the Runner earns for delivering an order whole (Rule Two, amended by ruling 60): five
 * pounds, or `largeOrderRunnerPaymentPence` (£7) when the shop prices come to
 * `largeOrderFromPence` (£120) or more. A part of a split job pays its own figure instead.
 */
export function runnerPaymentFor(goodsPence, fees) {
    return goodsPence >= fees.largeOrderFromPence
        ? fees.largeOrderRunnerPaymentPence
        : RUNNER_PAYMENT_PENCE;
}
/**
 * A basket over the most one order carries, priced as linked orders (ruling 61, Anthony,
 * 10 October 2026). The first order is filled with items up to `maximumOrderGoodsPence` (£150),
 * then the next up to £150, and so on, dearest first; no product is ever split. The first order
 * keeps the Shopper's own delivery for its plan; every order after it costs
 * `extraRunnerDeliveryPence` (£13.50) whatever the plan. A basket within one order is one part,
 * priced exactly as `priceBasket`. Refuses a basket over `maximumBasketGoodsPence` (£450).
 *
 * So £172 of shopping on pay as you go is £150 with £13.50 delivery and £22 with £13.50 more.
 */
export function priceBasketInParts(lines, plan, fees, allowanceExtraPence = 0) {
    assertWholePence(allowanceExtraPence, 'Extra delivery charge');
    for (const line of lines)
        assertProductAllowed(line.unitPricePence, fees);
    const goods = lines.reduce((sum, line) => sum + line.unitPricePence * line.quantity, 0);
    if (goods > fees.maximumBasketGoodsPence) {
        throw new BasketOverMaximumError(goods, fees.maximumBasketGoodsPence);
    }
    // The most one order carries is counted on what the Runner buys at the till (30 October 2026):
    // the counter price of a partner's thing, otherwise its shop price.
    const carriedOf = (line) => line.counterPricePence ?? line.unitPricePence;
    const carried = lines.reduce((sum, line) => sum + carriedOf(line) * line.quantity, 0);
    const byKey = new Map(lines.map((line) => [line.key, line]));
    const packed = (carried <= fees.maximumOrderGoodsPence
        ? [{ lines: lines.map(({ key, quantity }) => ({ key, quantity })) }]
        : packFirstFit(lines.map((line) => ({
            key: line.key,
            quantity: line.quantity,
            unitPricePence: carriedOf(line),
        })), fees.maximumOrderGoodsPence, () => ({ tag: null, capacityPence: fees.maximumOrderGoodsPence }))).map((bin) => {
        const binLines = bin.lines.map(({ key, quantity }) => ({
            key,
            quantity,
            unitPricePence: byKey.get(key).unitPricePence,
            counterPricePence: byKey.get(key).counterPricePence,
        }));
        return {
            lines: binLines.map(({ key, quantity, unitPricePence }) => ({
                key,
                quantity,
                unitPricePence,
            })),
            goodsPence: binLines.reduce((sum, line) => sum + line.unitPricePence * line.quantity, 0),
            carriedPence: binLines.reduce((sum, line) => sum + (line.counterPricePence ?? line.unitPricePence) * line.quantity, 0),
        };
    });
    const parts = packed.map((bin, index) => {
        const itemChargesPence = itemChargesForLines(bin.lines.map((line) => ({ shopPricePence: line.unitPricePence, quantity: line.quantity })), fees);
        const extra = index > 0;
        const feePence = extra
            ? fees.extraRunnerDeliveryPence
            : deliveryFeePence(bin.goodsPence, plan, fees, bin.carriedPence);
        // A basket kept whole is one order of the month (ruling 73): any extra charge for the
        // allowance is on the first order only.
        const partExtra = index === 0 ? allowanceExtraPence : 0;
        return {
            part: index + 1,
            lines: bin.lines,
            extra,
            goodsPence: bin.goodsPence,
            itemChargesPence,
            feePence,
            allowanceExtraPence: partExtra,
            totalPence: bin.goodsPence + itemChargesPence + feePence + partExtra,
            plan,
        };
    });
    const sum = (pick) => parts.reduce((total, part) => total + pick(part), 0);
    return {
        parts,
        goodsPence: goods,
        itemChargesPence: sum((part) => part.itemChargesPence),
        feePence: sum((part) => part.feePence),
        allowanceExtraPence: sum((part) => part.allowanceExtraPence),
        totalPence: sum((part) => part.totalPence),
        extraDeliveryPence: sum((part) => (part.extra ? part.feePence : 0)),
        plan,
    };
}
/**
 * What the Shopper is told, before paying, when their basket is over one order (ruling 61):
 * plainly and gently, with both choices.
 */
export function overOneRunnerWords(fees, formatMoney) {
    // "over £150", as Anthony said it: whole pounds without the pence.
    const most = formatMoney(fees.maximumOrderGoodsPence).replace(/\.00$/, '');
    return `Your shopping is over ${most}, which is more than one Runner can carry. You can take something out or swap it to stay with one Runner, or keep everything and a second Runner will bring the rest for an extra ${formatMoney(fees.extraRunnerDeliveryPence)} delivery.`;
}
/** The full economics of an order, for internal reporting. */
export function orderEconomics(pricing, processor) {
    const cost = processorCostPence(pricing.totalPence, processor);
    return {
        ...pricing,
        runnerPaymentPence: RUNNER_PAYMENT_PENCE,
        processorCostPence: cost,
        platformNetPence: pricing.feePence -
            RUNNER_PAYMENT_PENCE +
            pricing.itemChargesPence +
            pricing.allowanceExtraPence -
            cost,
    };
}
/**
 * An organisation's monthly charge for this many clients: the sum over clients numbered 1 to N
 * of the discounted price for every Nth one and the full price for everyone else.
 */
export function organisationMonthlyPence(clients, pricing) {
    assertWholePence(clients, 'Number of clients');
    const discounted = Math.floor(clients / pricing.discountEveryNthClient);
    return ((clients - discounted) * pricing.clientMonthlyPence +
        discounted * pricing.discountedClientMonthlyPence);
}
//# sourceMappingURL=fees.js.map